/**
 * The movement simulation, run at 20Hz on both sides.
 *
 * The server runs this for every entity, and the client runs the identical code for
 * the player it holds so movement feels instant. Whenever the server's result differs
 * from the prediction, the client takes the server's answer and walks off the
 * difference. Keeping one implementation is what keeps the two in agreement.
 *
 * Everything here is tile bound. An entity is always walking from one tile centre to
 * the next, and anything that stops it finishes the step it was taking first, so a
 * character is never left standing between two tiles. That is not a detail of the
 * animation: the whole game is measured in tiles, and half a tile out is the
 * difference between being in reach of something and not.
 */

import type { CollisionMap } from './collision';
import { MAX_RUN_ENERGY, RUN_SPEED, WALK_SPEED } from './constants';
import { centerOf, headingOf, tileOf, type WorldPos } from './tiles';

/** Distance at which a waypoint counts as reached. */
const WAYPOINT_EPSILON = 0.05;

/** How far off a tile centre an entity may be and still count as standing on it. */
const CENTRE_EPSILON = 0.01;

/**
 * Fraction of a step's travel budget that has to be covered for the step to count as
 * progress. Sliding along a wall covers less ground than a step in the open, so this
 * is generous: it is looking for an entity wedged against geometry, not a slow one.
 */
const PROGRESS_FRACTION = 0.2;

/**
 * Consecutive stalled steps before a path is abandoned. Half a second of getting
 * nowhere, which is long enough that rounding at a corner never triggers it.
 */
const STUCK_STEP_LIMIT = 10;

/** Run energy drained per second of running. Full energy lasts roughly 60 seconds. */
const ENERGY_DRAIN_PER_SECOND = MAX_RUN_ENERGY / 60;

/**
 * The least energy running can be switched on with: one percent of a full bar.
 *
 * Running stops by itself at nothing, and stays stopped until somebody turns it
 * back on. Without a floor to turn it on from, the sliver of energy that comes
 * back while standing for a moment was enough to run one step, drain, walk, and
 * run again: to everybody watching, a character flickering between running and
 * walking at a walking pace.
 */
export const MIN_RUN_ENERGY = MAX_RUN_ENERGY / 100;

/** Whether running can be switched on, or kept on, with the energy there is. */
export function runAllowed(
  state: { running: boolean; runEnergy: number },
  wanted: boolean,
): boolean {
  if (!wanted) return false;
  // Already running keeps going to the last drop; starting needs a whole point.
  return state.running ? state.runEnergy > 0 : state.runEnergy >= MIN_RUN_ENERGY;
}

export interface MovementState {
  pos: WorldPos;
  /** Facing angle in radians. Visual only, never read by game logic. */
  heading: number;
  /** Remaining waypoints, each one the centre of a tile. */
  path: WorldPos[];
  /** Whether run is toggled on. Running still requires energy. */
  running: boolean;
  runEnergy: number;
  /**
   * Consecutive movement steps that covered almost no ground while following a path.
   * Derived state, never sent over the wire: both sides run this code from the same
   * inputs, so both arrive at the same count.
   */
  stuckSteps: number;
}

export function createMovementState(pos: WorldPos): MovementState {
  return {
    pos: { ...pos },
    heading: 0,
    path: [],
    running: false,
    runEnergy: MAX_RUN_ENERGY,
    stuckSteps: 0,
  };
}

/** Starts a new route. Always use this rather than assigning `path` directly. */
export function setPath(state: MovementState, points: WorldPos[]): void {
  state.path = points;
  state.stuckSteps = 0;
}

/** Whether a position is on a tile centre rather than partway between two. */
export function isCentred(pos: WorldPos): boolean {
  return (
    Math.abs(pos.x - Math.floor(pos.x) - 0.5) < CENTRE_EPSILON &&
    Math.abs(pos.z - Math.floor(pos.z) - 0.5) < CENTRE_EPSILON
  );
}

/**
 * Stops, on a tile.
 *
 * Whatever the reason for stopping, the step being taken is finished first. Cutting
 * it short is what left characters standing on the line between two tiles, halfway
 * inside whatever they were walking around.
 */
export function haltAtTile(state: MovementState): void {
  state.stuckSteps = 0;

  const next = state.path[0];
  if (next) {
    state.path = [next];
    return;
  }

  state.path = isCentred(state.pos) ? [] : [centerOf(tileOf(state.pos))];
}

/** One input frame from a client. Sequence numbers let the server acknowledge progress. */
export interface InputFrame {
  sequence: number;
  running: boolean;
}

export function currentSpeed(state: MovementState): number {
  return state.running && state.runEnergy > 0 ? RUN_SPEED : WALK_SPEED;
}

/**
 * Advances one movement step. Returns true when the entity actually moved, which the
 * caller uses to decide whether a position update needs to go out.
 */
export function stepMovement(map: CollisionMap, state: MovementState, dt: number): boolean {
  const speed = currentSpeed(state);
  let budget = speed * dt;
  const startX = state.pos.x;
  const startZ = state.pos.z;

  // Standing still is standing on a tile. Anything that leaves an entity off centre,
  // whether a correction from the server or a slide along a wall, is walked off
  // before it can be mistaken for a position.
  if (state.path.length === 0 && !isCentred(state.pos)) {
    state.path = [centerOf(tileOf(state.pos))];
  }

  while (budget > 0 && state.path.length > 0) {
    const target = state.path[0];
    if (!target) break;

    const dx = target.x - state.pos.x;
    const dz = target.z - state.pos.z;
    const distance = Math.hypot(dx, dz);

    if (distance <= WAYPOINT_EPSILON) {
      state.pos = { ...target };
      state.path.shift();
      continue;
    }

    const travel = Math.min(budget, distance);
    const before = state.pos;
    state.pos = map.resolveMovement(state.pos, (dx / distance) * travel, (dz / distance) * travel);
    budget -= travel;

    if (Math.hypot(target.x - state.pos.x, target.z - state.pos.z) <= WAYPOINT_EPSILON) {
      // Landing on the centre exactly, rather than within a hair of it, is what
      // keeps a long walk from accumulating a drift out of many short legs.
      state.pos = { ...target };
      state.path.shift();
      continue;
    }

    // Geometry stopped this leg. Give up on the rest of the budget this step, but
    // keep the route: judging a whole path on one obstructed leg is what used to
    // throw the route away the moment a player rounded the first corner.
    const advanced = Math.hypot(state.pos.x - before.x, state.pos.z - before.z);
    if (advanced < travel * PROGRESS_FRACTION) break;
  }

  // A path is only abandoned once the entity has spent long enough covering no
  // ground that it is clearly wedged rather than sliding along something.
  if (state.path.length > 0) {
    const covered = Math.hypot(state.pos.x - startX, state.pos.z - startZ);
    if (covered < speed * dt * PROGRESS_FRACTION) {
      state.stuckSteps++;
      if (state.stuckSteps >= STUCK_STEP_LIMIT) {
        state.path.length = 0;
        state.stuckSteps = 0;
      }
    } else {
      state.stuckSteps = 0;
    }
  }

  const movedX = state.pos.x - startX;
  const movedZ = state.pos.z - startZ;
  const moved = Math.abs(movedX) > 1e-6 || Math.abs(movedZ) > 1e-6;

  if (moved) {
    state.heading = headingOf(movedX, movedZ);
    if (state.running) {
      state.runEnergy = Math.max(0, state.runEnergy - ENERGY_DRAIN_PER_SECOND * dt);
      // Out of breath is the end of running, on both sides of the wire at once,
      // because both run this same step.
      if (state.runEnergy <= 0) state.running = false;
    }
  }

  return moved;
}

/**
 * Restores run energy while an entity is not running. The rate scales with Agility,
 * so a level 1 player recovers slowly and a level 99 player recovers roughly three
 * times as fast.
 */
export function restoreRunEnergy(state: MovementState, agilityLevel: number, dt: number): void {
  if (state.runEnergy >= MAX_RUN_ENERGY) return;
  const perSecond = (MAX_RUN_ENERGY / 180) * (1 + agilityLevel / 50);
  state.runEnergy = Math.min(MAX_RUN_ENERGY, state.runEnergy + perSecond * dt);
}
